import { useState, useEffect } from 'react';
import { useTranslation } from '../i18n';
import CardNB from './ui-nb/CardNB';
import ButtonNB from './ui-nb/ButtonNB';
import { supabase } from '../lib/supabase';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [isOAuthUser, setIsOAuthUser] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<ModalStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const checkProvider = async () => {
      const { data } = await supabase.auth.getSession();
      const provider = data?.session?.user?.app_metadata?.provider;
      setIsOAuthUser(provider === 'google');
    };
    checkProvider();
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setErrorMessage(t('changePassword.passwordMismatch', 'New passwords do not match.'));
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setStatus('success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err) {
      setErrorMessage(t('changePassword.error', 'Failed to update password. Please check your current password.'));
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={onClose}
    >
      <CardNB
        className="max-w-sm w-full mx-4"
        style={{ border: '3px solid #000' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-center mb-4" style={{ color: '#000' }}>
          {t('changePassword.title', 'Change Password')}
        </h2>

        {isOAuthUser ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ color: '#4A4A4A', fontSize: '0.875rem', lineHeight: 1.6 }}>
              {t('changePassword.oauthMessage', 'You signed in with Google. Password change is not applicable for Google accounts.')}
            </p>
            <ButtonNB variant="secondary" onClick={onClose} className="mt-4 w-full">
              {t('common.close', 'Close')}
            </ButtonNB>
          </div>
        ) : status === 'success' ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ color: '#000000', fontWeight: 500 }}>
              {t('changePassword.success', 'Password updated successfully.')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label htmlFor="cp-current" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#000' }}>
                {t('changePassword.currentPassword', 'Current password')}
              </label>
              <input
                id="cp-current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #000', fontSize: '0.875rem' }}
              />
            </div>

            <div>
              <label htmlFor="cp-new" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#000' }}>
                {t('changePassword.newPassword', 'New password')}
              </label>
              <input
                id="cp-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #000', fontSize: '0.875rem' }}
              />
            </div>

            <div>
              <label htmlFor="cp-confirm" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#000' }}>
                {t('changePassword.confirmPassword', 'Confirm new password')}
              </label>
              <input
                id="cp-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #000', fontSize: '0.875rem' }}
              />
            </div>

            {errorMessage && (
              <p style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errorMessage}</p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <ButtonNB
                type="submit"
                variant="primary"
                disabled={status === 'loading'}
                loading={status === 'loading'}
                style={{ flex: 1 }}
              >
                {status === 'loading'
                  ? t('changePassword.submitting', 'Updating...')
                  : t('changePassword.submit', 'Update password')}
              </ButtonNB>
              <ButtonNB type="button" variant="secondary" onClick={onClose}>
                {t('common.cancel', 'Cancel')}
              </ButtonNB>
            </div>
          </form>
        )}
      </CardNB>
    </div>
  );
}
